import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import uniqueId from "lodash/uniqueId";
import React, {
  AnimationEvent,
  ChangeEvent,
  HTMLAttributes,
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { CustomComponents, DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import usePrefersReducedMotion from "../../../shared/hooks/usePrefersReducedMotion";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaCloseButton from "../kuika-close-button/kuika-close-button";
import KuikaSwap from "../kuika-swap/kuika-swap";
import "./kuika-date-picker.scss";

dayjs.extend(customParseFormat);

// UIKit date / datetime / datetime-offset primitive.
//
// Shadcn-style composition: a single Button trigger that displays
// the formatted value, opens a Popover containing a DayPicker
// calendar (and a time input for datetime / datetimeoffset modes).
// No visible native `<input type="date">` — those render in user-
// locale text (MM/DD/YYYY for en-US, DD.MM.YYYY for de-DE, etc.),
// which is the exact problem this rewrite solves. Display is fully
// under our control via dayjs.
//
// "DB-tool" semantics: the value displayed is the wall clock stored
// in the row. We do NOT convert into the browser's local timezone
// for display, and we do NOT stamp the browser offset on save.
//
// Three modes:
//   * `date` — calendar only. Value shape: `YYYY-MM-DD`.
//   * `datetime` — date + time, naive (no offset). Value shape:
//     `YYYY-MM-DDTHH:MM:SS[.fff]`.
//   * `datetimeoffset` — same as datetime; the caller is responsible
//     for stamping the offset on save (typically `+00:00` for UTC).
//     The display strips any trailing offset / Z suffix.

export type KuikaDatePickerMode = "date" | "datetime" | "datetimeoffset";

// Visible display formats — DD/MM/YYYY day-first throughout.
const DATE_DISPLAY_FORMAT: string = "DD/MM/YYYY";
const DATE_ISO_FORMAT: string = "YYYY-MM-DD";

export interface IKuikaDatePickerProps {
  mode: KuikaDatePickerMode;
  // Raw stored value. The picker strips any trailing offset / Z
  // and normalises fractional seconds to exactly 3 digits.
  value: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  // True when the source data has fractional seconds — controls the
  // time input's `step` so the picker accepts ms input.
  hasFractionalSeconds?: boolean;
  size?: "sm" | "md";
  // "cell" — flat in-grid fill (no inset border). "standalone" — form
  // control with outline. Default "standalone".
  variant?: "standalone" | "cell";
  // Show an explicit × clear button when there's a value.
  showClear?: boolean;
  // Fill the host's width, as a KuikaInput in the same form column does. By default the standalone
  // control is as wide as its label.
  block?: boolean;
  onChange: (next: string | null) => void;
  onBlur?: () => void;
}

interface IDatetimeParts {
  base: string;
  fractional: string | null;
}

const splitFraction = (raw: string): IDatetimeParts => {
  if (raw.length < 20 || raw[19] !== ".") {
    return { base: raw.slice(0, 19), fractional: null };
  }
  let i: number = 20;
  while (i < raw.length && raw[i] >= "0" && raw[i] <= "9") {
    i += 1;
  }
  return { base: raw.slice(0, 19), fractional: raw.slice(20, i) };
};

const dateHalfOf = (raw: string): string => raw.slice(0, 10);

const timeHalfOf = (raw: string, hasFractional: boolean): string => {
  if (raw.length < 19) {
    return "";
  }
  const parts: IDatetimeParts = splitFraction(raw);
  const seconds: string = parts.base.slice(11, 19);
  if (!hasFractional || parts.fractional === null) {
    return seconds;
  }
  const padded: string = (parts.fractional + "000").slice(0, 3);
  return `${seconds}.${padded}`;
};

// Format the stored ISO value for display on the trigger button.
// Date mode shows DD/MM/YYYY; datetime modes show DD/MM/YYYY HH:MM:SS.
// Empty value returns empty so the placeholder can render.
const formatForDisplay = (raw: string, isDateOnly: boolean): string => {
  if (raw.length === 0) {
    return "";
  }
  const date: string = dateHalfOf(raw);
  const parsed: dayjs.Dayjs = dayjs(date, DATE_ISO_FORMAT, true);
  if (!parsed.isValid()) {
    return raw;
  }
  const datePart: string = parsed.format(DATE_DISPLAY_FORMAT);
  if (isDateOnly) {
    return datePart;
  }
  const time: string = timeHalfOf(raw, false);
  if (time.length === 0) {
    return datePart;
  }
  // Drop trailing :00 seconds for visual compactness on the button —
  // "12:34" reads cleaner than "12:34:00" in the cell. The popover's
  // time input still operates at full HH:MM:SS precision.
  const compactTime: string = time.endsWith(":00") ? time.slice(0, 5) : time;
  return `${datePart} ${compactTime}`;
};

const parseIsoToDate = (iso: string): Date | undefined => {
  if (iso.length === 0) {
    return undefined;
  }
  const parsed: dayjs.Dayjs = dayjs(iso, DATE_ISO_FORMAT, true);
  if (!parsed.isValid()) {
    return undefined;
  }
  return parsed.toDate();
};

const dateToIso = (date: Date): string => dayjs(date).format(DATE_ISO_FORMAT);

const joinDateAndTime = (date: string, time: string): string | null => {
  if (date.length === 0 || time.length === 0) {
    return null;
  }
  let normalisedTime: string = time;
  if (normalisedTime.length === 5) {
    normalisedTime = `${normalisedTime}:00`;
  }
  return `${date}T${normalisedTime}`;
};

// ── Month change motion ──────────────────────────────────────────
// Paging a month plays on the one day grid, in sequence: the current
// month leaves (`leaving`), the new one is committed to DayPicker and
// enters (`entering`), then the grid rests (`idle`). The direction
// picks which way both halves travel. Keyboard paging from inside the
// grid and reduced motion commit at once — the focused day must exist
// in the new month straight away.
type MonthMotionPhase = "idle" | "leaving" | "entering";
type MonthMotionDirection = "next" | "prev";

const GRID_SELECTOR: string = ".rdp-month_grid";
const MONTH_MOTION_KEYFRAMES: string[] = ["kuika-pane-in", "kuika-pane-out"];

const monthIndexOf = (date: Date): number => date.getFullYear() * 12 + date.getMonth();

const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

// The caption swaps with the grid; DayPicker renders it, so the
// direction reaches it through context.
const MonthDirectionContext: React.Context<MonthMotionDirection> = createContext<MonthMotionDirection>("next");

const MonthCaptionLabel = (props: HTMLAttributes<HTMLSpanElement>): JSX.Element => {
  const direction: MonthMotionDirection = useContext(MonthDirectionContext);
  return (
    <span {...props}>
      <KuikaSwap contentKey={String(props.children)} direction={direction === "next" ? "up" : "down"}>
        {props.children}
      </KuikaSwap>
    </span>
  );
};

const DAY_PICKER_COMPONENTS: Partial<CustomComponents> = { CaptionLabel: MonthCaptionLabel };

const KuikaDatePicker: React.FC<IKuikaDatePickerProps> = (props: IKuikaDatePickerProps): JSX.Element => {
  const {
    mode,
    value,
    disabled,
    placeholder,
    className,
    hasFractionalSeconds,
    size = "md",
    variant = "standalone",
    showClear = false,
    block = false,
    onChange,
    onBlur
  } = props;

  const isDateOnly: boolean = mode === "date";
  const storedDate: string = dateHalfOf(value);
  const storedTime: string = isDateOnly ? "" : timeHalfOf(value, hasFractionalSeconds === true);
  const displayText: string = formatForDisplay(value, isDateOnly);
  const hasAnyValue: boolean = storedDate.length > 0 || storedTime.length > 0;

  const [open, setOpen] = useState<boolean>(false);
  const [month, setMonth] = useState<Date>((): Date => startOfMonth(parseIsoToDate(storedDate) ?? new Date()));
  const [pendingMonth, setPendingMonth] = useState<Date | null>(null);
  const [phase, setPhase] = useState<MonthMotionPhase>("idle");
  const [direction, setDirection] = useState<MonthMotionDirection>("next");
  const isReducedMotion: boolean = usePrefersReducedMotion();
  const popRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  // Set by a key press inside the day grid (arrow keys, Page Up/Down crossing a month edge), read
  // by the month change it causes. Not focus-based: a click on the nav buttons leaves focus on the
  // grid in browsers that do not focus buttons on click.
  const gridKeyPagingRef: React.MutableRefObject<boolean> = useRef<boolean>(false);
  // Per instance: two datetime pickers on one page must not share the label's target.
  const [timeInputId] = useState<string>((): string => uniqueId("kuika-date-picker-time-"));

  const handleOpenChange = (nextOpen: boolean): void => {
    if (nextOpen) {
      // Open on the stored value's month (today when empty), at rest.
      setMonth(startOfMonth(parseIsoToDate(storedDate) ?? new Date()));
      setPendingMonth(null);
      setPhase("idle");
    }
    setOpen(nextOpen);
  };

  const handleMonthChange = (nextMonth: Date): void => {
    // DayPicker pages from the month it shows, which lags behind while the old grid is leaving;
    // apply its step to the pending month so a second click moves on instead of repeating.
    const step: number = monthIndexOf(nextMonth) - monthIndexOf(month);
    const from: Date = pendingMonth ?? month;
    const target: Date = new Date(from.getFullYear(), from.getMonth() + step, 1);
    if (step === 0) {
      return;
    }
    setDirection(monthIndexOf(target) > monthIndexOf(from) ? "next" : "prev");
    const isPagingFromGrid: boolean = gridKeyPagingRef.current;
    gridKeyPagingRef.current = false;
    if (isReducedMotion || isPagingFromGrid) {
      setMonth(target);
      setPendingMonth(null);
      setPhase(isReducedMotion ? "idle" : "entering");
      return;
    }
    // A click while the old month is still leaving only retargets it.
    setPendingMonth(target);
    setPhase("leaving");
  };

  const commitPendingMonth = (): void => {
    if (pendingMonth === null) {
      return;
    }
    setMonth(pendingMonth);
    setPendingMonth(null);
    setPhase("entering");
  };

  // Safety net: when no leave animation applies to the grid (its
  // stylesheet failed, or a consumer overrode it), commit at once
  // instead of waiting for an animationend that never comes.
  useLayoutEffect((): void => {
    if (phase !== "leaving") {
      return;
    }
    const grid: Element | null = popRef.current?.querySelector(GRID_SELECTOR) ?? null;
    if (grid !== null && window.getComputedStyle(grid).animationName === "none") {
      commitPendingMonth();
    }
  });

  const handleGridAnimationEnd = (event: AnimationEvent<HTMLDivElement>): void => {
    const grid: Element | null = event.target instanceof Element ? event.target.closest(GRID_SELECTOR) : null;
    if (grid === null || event.target !== grid || !MONTH_MOTION_KEYFRAMES.includes(event.animationName)) {
      return;
    }
    if (phase === "leaving") {
      commitPendingMonth();
      return;
    }
    if (phase === "entering") {
      setPhase("idle");
    }
  };

  const handleCalendarSelect = (selected: Date | undefined): void => {
    if (selected === undefined) {
      // Clicking the currently-selected day in DayPicker fires
      // undefined (toggle). Treat as no-op so a stray double-click
      // doesn't clear the value; the × button is the only path to
      // null.
      return;
    }
    const nextDate: string = dateToIso(selected);
    if (isDateOnly) {
      onChange(nextDate);
      setOpen(false);
      return;
    }
    // Datetime / dto — keep the existing time half, default to
    // midnight if the cell was previously empty.
    const time: string = storedTime.length > 0 ? storedTime : "00:00:00";
    onChange(joinDateAndTime(nextDate, time));
    // Don't auto-close on datetime so the user can adjust the time
    // in the same popover open. Date mode closes (typical
    // single-click flow); datetime stays open until the user
    // dismisses explicitly or hits Done.
  };

  const handleTimeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const nextTime: string = e.target.value;
    if (storedDate.length === 0) {
      // Time edited before a date is picked — default to today so
      // the value can land somewhere. Avoids dropping the user's
      // typed time on the floor.
      const today: string = dayjs().format(DATE_ISO_FORMAT);
      onChange(joinDateAndTime(today, nextTime));
      return;
    }
    onChange(joinDateAndTime(storedDate, nextTime));
  };

  const clear = (): void => {
    onChange(null);
  };

  const wrapperClass: string = classNames(
    "kuika-date-picker",
    `kuika-date-picker--${variant}`,
    `kuika-date-picker--${size}`,
    `kuika-date-picker--${mode}`,
    {
      "kuika-date-picker--disabled": disabled,
      "kuika-date-picker--block": block,
      "kuika-date-picker--empty": !hasAnyValue,
      "kuika-date-picker--has-value": hasAnyValue
    },
    className
  );

  const triggerPlaceholder: string =
    placeholder !== undefined && placeholder.length > 0
      ? placeholder
      : isDateOnly
        ? "Pick a date"
        : "Pick a date and time";

  return (
    <span className={wrapperClass}>
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="kuika-date-picker__trigger"
            disabled={disabled}
            aria-label={hasAnyValue ? `${triggerPlaceholder} — ${displayText}` : triggerPlaceholder}
            onBlur={onBlur}
          >
            <svg
              className="kuika-date-picker__trigger-icon"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              aria-hidden="true"
            >
              <rect
                x="1.5"
                y="2.5"
                width="11"
                height="10"
                rx="1.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
              />
              <line x1="1.5" y1="5.5" x2="12.5" y2="5.5" stroke="currentColor" strokeWidth="1.2" />
              <line x1="4" y1="1" x2="4" y2="4" stroke="currentColor" strokeWidth="1.2" />
              <line x1="10" y1="1" x2="10" y2="4" stroke="currentColor" strokeWidth="1.2" />
            </svg>
            <span className="kuika-date-picker__trigger-label">{hasAnyValue ? displayText : triggerPlaceholder}</span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={classNames("kuika-date-picker__pop", `kuika-date-picker__pop--${direction}`, {
              "kuika-date-picker__pop--leaving": phase === "leaving",
              "kuika-date-picker__pop--entering": phase === "entering"
            })}
            side="bottom"
            align="start"
            sideOffset={6}
            onAnimationEnd={handleGridAnimationEnd}
            onKeyDownCapture={(event: React.KeyboardEvent<HTMLDivElement>): void => {
              gridKeyPagingRef.current =
                event.target instanceof Element && event.target.closest(GRID_SELECTOR) !== null;
            }}
            onPointerDownCapture={(): void => {
              // A key press inside the grid that stayed within the month must not make the next
              // click on ‹ › skip its slide.
              gridKeyPagingRef.current = false;
            }}
            ref={popRef}
          >
            <MonthDirectionContext.Provider value={direction}>
              <DayPicker
                mode="single"
                selected={parseIsoToDate(storedDate)}
                onSelect={handleCalendarSelect}
                month={month}
                onMonthChange={handleMonthChange}
                weekStartsOn={1}
                formatters={{
                  formatCaption: (date: Date): string => dayjs(date).format("MMMM YYYY")
                }}
                components={DAY_PICKER_COMPONENTS}
                showOutsideDays
              />
            </MonthDirectionContext.Provider>
            {!isDateOnly && (
              <div className="kuika-date-picker__pop-time">
                <label className="kuika-date-picker__pop-time-label" htmlFor={timeInputId}>
                  Time
                </label>
                <input
                  id={timeInputId}
                  type="time"
                  className="kuika-date-picker__pop-time-input"
                  step={hasFractionalSeconds === true ? 0.001 : 1}
                  value={storedTime}
                  onChange={handleTimeChange}
                />
              </div>
            )}
            <div className="kuika-date-picker__pop-actions">
              {showClear && hasAnyValue && (
                <KuikaButton
                  type="text"
                  size="sm"
                  color="danger"
                  className="kuika-date-picker__pop-clear"
                  onClick={(): void => {
                    clear();
                    setOpen(false);
                  }}
                >
                  Clear
                </KuikaButton>
              )}
              <KuikaButton type="primary" size="sm" onClick={(): void => setOpen(false)}>
                Done
              </KuikaButton>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {showClear && hasAnyValue && !disabled && (
        <KuikaCloseButton
          size="sm"
          className="kuika-date-picker__clear"
          aria-label="Clear"
          title="Clear (set to null)"
          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>): void => e.preventDefault()}
          onClick={clear}
        />
      )}
    </span>
  );
};

export default KuikaDatePicker;
